import React from 'react';
import { corridaEnCurso } from './metricas';
import type { RunResumen } from './tipos';
import { ChipStatus, formatearFecha } from './ui';

/**
 * Lista de corridas con checkbox: mínimo 2, máximo `max`. El orden de selección es el de
 * las columnas. Las corridas EN CURSO no se pueden elegir: su detalle se cachea al
 * elegirlas y no se vuelve a pedir, así que quedaría con filas pendientes y métricas a
 * medias (y se podría lanzar una comparación IA sobre resultados incompletos). Se
 * habilitan solas cuando terminan (Comparar refresca la lista mientras haya alguna).
 */
const SeleccionCorridas: React.FC<{
  runs: RunResumen[];
  seleccion: string[];
  max: number;
  onCambiar: (ids: string[]) => void;
}> = ({ runs, seleccion, max, onCambiar }) => {
  const toggle = (id: string) => {
    if (seleccion.includes(id)) onCambiar(seleccion.filter((x) => x !== id));
    else if (seleccion.length < max) onCambiar([...seleccion, id]);
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-brand-black">Corridas a comparar</h3>
        <span className="text-xs text-gray-500">
          {seleccion.length} elegidas · mínimo 2, máximo {max}
          {runs.some(corridaEnCurso) && ' · las corridas en curso se habilitan al terminar'}
        </span>
        {seleccion.length > 0 && (
          <button type="button" onClick={() => onCambiar([])} className="text-xs text-blue-600 underline">
            Ninguna
          </button>
        )}
      </div>
      {runs.length === 0 && <p className="text-sm text-gray-500">Sin corridas todavía.</p>}
      {runs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                {['', 'Etiqueta', 'Modelo', 'Effort', 'Fotos', 'Fecha', 'Progreso', 'Estado'].map((h, i) => (
                  <th key={i} className="py-1 pr-3 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const marcada = seleccion.includes(r.run_id);
                const enCurso = corridaEnCurso(r);
                const bloqueada = enCurso || (!marcada && seleccion.length >= max);
                return (
                  <tr
                    key={r.run_id}
                    title={enCurso ? 'Todavía en curso: se puede comparar cuando termine' : undefined}
                    onClick={() => !bloqueada && toggle(r.run_id)}
                    className={`border-t border-gray-100 ${bloqueada ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'} ${marcada ? 'bg-gray-50 font-medium' : ''}`}
                  >
                    <td className="py-1.5 pr-3">
                      <input type="checkbox" checked={marcada} disabled={bloqueada} onChange={() => toggle(r.run_id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 accent-brand-black" />
                    </td>
                    <td className="py-1.5 pr-3 text-brand-black">
                      {marcada && <span className="mr-1 text-[10px] text-gray-400">#{seleccion.indexOf(r.run_id) + 1}</span>}
                      {r.etiqueta}
                      {r.prompt_override && <span className="ml-1 text-[10px] text-gray-400">(prompt propio)</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-brand-black">{r.modelo}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.effort ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.fotos_producto}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{formatearFecha(r.created_at)}</td>
                    <td className="py-1.5 pr-3 tabular-nums text-brand-black">
                      {r.hechas}/{r.total}
                    </td>
                    <td className="py-1.5 pr-3">
                      <ChipStatus status={r.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SeleccionCorridas;
